import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '@/api';
import { ApiError } from '@/api/errors';
import { profileErrorMessage } from '@/api/reasons';
import { useCatalog } from '@/api/queries';
import type { Gender, MatchFilters, Me } from '@/api/types';
import { useAuth } from '@/stores/auth';
import { toast } from '@/stores/toasts';
import { Sheet } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { CountryMultiPicker } from '@/components/ui/CountryPicker';
import { countryList, flagEmoji, FILTER_GENDER_LABEL, MAX_FILTER_COUNTRIES } from '@/lib/countries';
import { cn } from '@/lib/cn';
import { formatCountdown } from '@/lib/clock';
import { FilterPassOffer } from '@/features/ads/RewardedOffers';
import { useFilterPass, useFilterPassLapse } from '@/features/ads/filterPass';
import { useAdsGate } from '@/features/ads/useAds';
import { useFinishProfile } from '@/stores/finishProfile';
import { profileIncomplete } from '@/features/onboarding/FinishProfile';

type FilterGender = Exclude<Gender, 'unspecified'>;
const FILTER_GENDERS: FilterGender[] = ['woman', 'man', 'nonbinary'];

export function hasFilters(f: MatchFilters | null | undefined): boolean {
  return !!f && (f.genders.length > 0 || f.countries.length > 0);
}

/** Short human summary: "Women · 🇫🇷 🇩🇪", or "Anyone, anywhere". */
export function filterSummary(f: MatchFilters | null | undefined): string {
  if (!hasFilters(f)) return 'Anyone, anywhere';
  const parts: string[] = [];
  if (f!.genders.length) parts.push(f!.genders.map((g) => (g === 'unspecified' ? '' : FILTER_GENDER_LABEL[g])).filter(Boolean).join(', '));
  if (f!.countries.length) parts.push(f!.countries.map(flagEmoji).join(' '));
  return parts.join(' · ');
}

export function isPlus(me: Me | null | undefined): boolean {
  return me?.plan === 'plus';
}

export function FiltersSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = useAuth((s) => s.me)!;
  const setMe = useAuth((s) => s.setMe);
  const catalog = useCatalog();
  const countries = countryList(catalog.data?.countries);
  const plus = isPlus(me);
  // v5: a rewarded filter pass unlocks filters for 30 minutes (SPEC_V5 §2).
  const pass = useFilterPass();
  const usable = plus || pass.active;
  const lapsed = useFilterPassLapse((s) => s.lapsedUntil);
  const ads = useAdsGate();
  const [genders, setGenders] = useState<Gender[]>(me.match_filters.genders);
  const [codes, setCodes] = useState<string[]>(me.match_filters.countries);
  const [busy, setBusy] = useState<'save' | 'clear' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setGenders(me.match_filters.genders);
    setCodes(me.match_filters.countries);
    setErr(null);
  }, [open, me.match_filters]);

  const toggleGender = (g: FilterGender) => setGenders((cur) => (cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]));
  const dirty = JSON.stringify([...genders].sort()) !== JSON.stringify([...me.match_filters.genders].sort()) || JSON.stringify([...codes].sort()) !== JSON.stringify([...me.match_filters.countries].sort());

  const save = async (next: MatchFilters, kind: 'save' | 'clear' = 'save') => {
    if (busy) return;
    setBusy(kind);
    setErr(null);
    try {
      const saved = await endpoints.setFilters(next);
      setMe(saved);
      toast.success(hasFilters(next) ? 'Filters saved' : 'Filters cleared', hasFilters(next) ? filterSummary(next) : 'You’ll meet anyone, anywhere.');
      onClose();
    } catch (e) {
      setErr(
        e instanceof ApiError && e.code === 'plus_required'
          ? ads.on
            ? 'Filters need Popin Plus or a running filter pass.'
            : 'Filters are part of Popin Plus.'
          : profileErrorMessage(e),
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Who would you like to meet?" description="Filters work both ways: you’ll only be matched with people whose filters include you too." testId="filters-sheet" maxHeight="92vh">
      {!plus && pass.active && (
        <div className="mb-5 rounded-card bg-raised p-4" data-testid="filter-pass-active">
          <p className="flex flex-wrap items-center gap-2 text-[15px] font-semibold">
            <FilterPassChip remainingMs={pass.remainingMs} /> Filters are on for you right now.
          </p>
          <p className="mt-1 text-sm text-muted">When the time runs out, filters quietly pause and your choices stay saved. Plus keeps them on all the time.</p>
          {pass.remainingMs <= 30 * 60_000 && <FilterPassOffer extend className="mt-3" />}
        </div>
      )}
      {!usable && (
        <div className="mb-5 rounded-card bg-raised p-4" data-testid="plus-locked">
          <p className="text-[17px] font-semibold">Filters are part of Plus</p>
          <p className="mt-1 text-sm text-muted">
            {lapsed && hasFilters(me.match_filters)
              ? 'Your filter pass ended, so filters are paused. Your choices are saved. '
              : hasFilters(me.match_filters)
                ? 'Your saved filters are paused until Plus is back. '
                : ''}
            Choose genders and up to 5 countries. Safety and games stay free for everyone.
          </p>
          <Link to="/plus" onClick={onClose} className="mt-3 inline-flex h-10 items-center gap-1.5 rounded-full bg-yellow px-4 text-sm font-semibold text-night transition-colors hover:bg-yellow/90" data-testid="plus-upsell">
            See Popin Plus <Icon name="chevron-right" size={16} />
          </Link>
          <FilterPassOffer className="mt-4 border-t border-line pt-4" />
        </div>
      )}
      <fieldset disabled={!usable || !!busy} className={cn(!usable && 'pointer-events-none select-none opacity-45')} aria-disabled={!usable}>
        <legend className="mb-2 text-sm font-semibold">Gender</legend>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            aria-pressed={genders.length === 0}
            onClick={() => setGenders([])}
            className={cn('h-10 rounded-full px-4 text-sm font-medium transition-[background-color,transform] duration-150 ease-out active:scale-[0.96]', genders.length === 0 ? 'bg-primary text-ground' : 'bg-raised hover:bg-line')}
            data-testid="filter-gender-any"
          >
            Anyone
          </button>
          {FILTER_GENDERS.map((g) => {
            const on = genders.includes(g);
            return (
              <button
                key={g}
                type="button"
                aria-pressed={on}
                onClick={() => toggleGender(g)}
                data-testid={`filter-gender-${g}`}
                className={cn('h-10 rounded-full px-4 text-sm font-medium transition-[background-color,transform] duration-150 ease-out active:scale-[0.96]', on ? 'bg-primary text-ground' : 'bg-raised hover:bg-line')}
              >
                {FILTER_GENDER_LABEL[g]}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-[13px] text-muted">People who chose “Prefer not to say” aren’t matched by a gender filter.</p>

        <p className="mb-2 mt-6 text-sm font-semibold">Countries</p>
        <CountryMultiPicker value={codes} onChange={setCodes} countries={countries} max={MAX_FILTER_COUNTRIES} disabled={!usable || !!busy} />
        <p className="mt-1 text-[13px] text-muted">People who hide their country aren’t matched by a country filter.</p>
      </fieldset>
      {err && (
        <p role="alert" className="mt-4 text-sm font-medium text-danger" data-testid="filters-error">
          {err}
        </p>
      )}
      <div className="mt-6 grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          accent="paper"
          onClick={() => void save({ genders: [], countries: [] }, 'clear')}
          loading={busy === 'clear'}
          disabled={!!busy || !hasFilters(me.match_filters)}
          data-testid="filters-clear"
        >
          Clear filters
        </Button>
        <Button accent="paper" onClick={() => void save({ genders, countries: codes })} loading={busy === 'save'} disabled={!usable || !dirty || !!busy} data-testid="filters-save">
          Save filters
        </Button>
      </div>
    </Sheet>
  );
}

/** The "Filters" control on the Quick Meet / Play tiles. */
export function FiltersButton({ tone, testId = 'filters-button' }: { tone: 'onPop' | 'onDark'; testId?: string }) {
  const me = useAuth((s) => s.me)!;
  const [open, setOpen] = useState(false);
  const plus = isPlus(me);
  const pass = useFilterPass();
  const usable = plus || pass.active;
  const active = usable && hasFilters(me.match_filters);
  return (
    <>
      <span className="inline-flex max-w-full flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => (profileIncomplete(me) ? useFinishProfile.getState().request() : setOpen(true))}
        data-testid={testId}
        data-locked={!usable || undefined}
        className={cn(
          'inline-flex h-10 max-w-full items-center gap-2 rounded-full px-4 text-sm font-semibold transition-[background-color,border-color,transform] duration-150 ease-out active:scale-[0.96] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          tone === 'onPop'
            ? cn('border focus-visible:outline-night', active ? 'border-night bg-night text-bone' : 'border-night/30 text-night hover:bg-night/10')
            : cn('focus-visible:outline-fg', active ? 'bg-primary text-ground' : 'bg-raised text-fg hover:bg-line'),
        )}
        aria-label={usable ? `Filters: ${filterSummary(me.match_filters)}` : 'Filters (Popin Plus)'}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
          <path d="M4 6h16M7 12h10M10 18h4" />
        </svg>
        <span className="truncate">{active ? filterSummary(me.match_filters) : 'Filters'}</span>
        {!usable && (
          <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold', tone === 'onPop' ? 'bg-night text-bone' : 'bg-yellow text-night')}>
            <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
            Plus
          </span>
        )}
      </button>
      {!plus && pass.active && <FilterPassChip remainingMs={pass.remainingMs} tone={tone} />}
      </span>
      <FiltersSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** "Filters · 24:13 left" — a running rewarded filter pass (SPEC_V5 §2). */
export function FilterPassChip({ remainingMs, tone = 'onDark' }: { remainingMs: number; tone?: 'onPop' | 'onDark' }) {
  return (
    <span
      className={cn(
        'inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[13px] font-semibold tabular-nums',
        tone === 'onPop' ? 'bg-night text-bone' : 'bg-yellow text-night',
      )}
      role="timer"
      aria-label={`Filter pass: ${formatCountdown(remainingMs)} left`}
      data-testid="filter-pass-chip"
    >
      <Icon name="clock" size={14} strokeWidth={2.2} /> Filters · {formatCountdown(remainingMs)} left
    </span>
  );
}
